import { Injectable, OnDestroy } from '@angular/core';
import { Store, select, Action } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import {
  fnCompare,
  shallowCompare,
  ReusableStreamProperty
} from '@ipreo/muni-shared';
import {
  NoData,
  NoDealOrders,
  NoFilteredDealOrders,
  AccountsDisabled,
  Reset
} from '../../state/negative-cases/actions';
import { NegativeCasesState } from '../../state/negative-cases/state';
import { GameDayRootReducerState } from '../../state/root.state';
import { NegativeCasesData } from '../models/negative-cases';
import { GamedayFiltersState } from '../../state/data/state';
import { GameDayModel } from '../../models/gameday-model';
import {
  getFullGamedayState,
  getNegativeCasesState
} from '../../state/data/reducer';

@Injectable()
export class NegativeCasesService implements OnDestroy {
  @ReusableStreamProperty
  public readonly messages: Observable<NegativeCasesState> = this.store.pipe(
    select(getNegativeCasesState)
  );

  private readonly gameDayData$: Observable<NegativeCasesData> = this.store.pipe(
    select(getFullGamedayState),
    map(
      ({
        data: {
          filters,
          filters: { searchByAccountManager },
          isLoading,
          deal,
          orders
        },
        tranches: { filtered }
      }): NegativeCasesData => ({
        deal,
        orders,
        filters,
        isLoading: isLoading,
        searchByAccountManager: searchByAccountManager,
        filteredOrders: filtered.orders,
        showAccountNames:
          deal && deal.gamedaySettings
            ? deal.gamedaySettings.showAccountNames
            : false
      })
    ),
    distinctUntilChanged(
      fnCompare(
        (state: NegativeCasesData) => ({
          ...state,
          // ToDo: Check out if we need this or just compare with issue object
          // because issue should be the same for order and other dependant filters
          deal: undefined
        }),
        shallowCompare
      )
    )
  ) as Observable<NegativeCasesData>;

  private readonly gameDayDataSubscription: Subscription = this.gameDayData$.subscribe(
    (data: NegativeCasesData) => this.publishEvents(data)
  );

  constructor(private store: Store<GameDayRootReducerState>) {}

  private hasNoSeriesSelected({ seriesId }: GamedayFiltersState): boolean {
    return !!(seriesId && seriesId.length === 0);
  }

  private hasNoDealOrders(orders: GameDayModel.Order[]): boolean {
    return !orders || orders.length === 0;
  }

  private hasNoFilteredOrders(
    deal: GameDayModel.Deal,
    filteredOrders: GameDayModel.Order[],
    { ignoreStocks, isSealedBid, seriesId }: GamedayFiltersState
  ): boolean {
    const { series }: { series: GameDayModel.Series[] } = deal;
    const someSeriesUnselected: boolean =
      !seriesId || series.some((s) => seriesId.indexOf(s.id) === -1);

    // tslint:disable:no-boolean-literal-compare
    const stockToggle: boolean = ignoreStocks === false;
    const sealedBid: boolean = isSealedBid === false;
    // tslint:enable:no-boolean-literal-compare

    return (
      (someSeriesUnselected || stockToggle || sealedBid) &&
      (!filteredOrders || filteredOrders.length === 0)
    );
  }

  private publishEvents({
    deal,
    orders,
    filteredOrders,
    filters,
    isLoading,
    showAccountNames
  }: NegativeCasesData) {
    const errorActions: Action[] = [];

    if (isLoading) {
      return;
    }

    if (this.hasNoSeriesSelected(filters)) {
      errorActions.push(new NoData());
    } else {
      if (this.hasNoDealOrders(orders)) {
        errorActions.push(new NoDealOrders());
      } else if (this.hasNoFilteredOrders(deal, filteredOrders, filters)) {
        errorActions.push(new NoFilteredDealOrders());
      }
      if (!showAccountNames) {
        errorActions.push(new AccountsDisabled());
      }
    }

    this.store.dispatch(new Reset());

    errorActions.forEach((action: Action) => {
      this.store.dispatch(action);
    });
  }

  ngOnDestroy() {
    this.gameDayDataSubscription.unsubscribe();
  }
}
